<template>
  <div class="bg-cream rounded-2xl overflow-hidden px-3 pb-3">

    <!-- Время приёма: кликабельна вся связка «текст + точки», не только иконка.
         Точки горизонтальные: вертикальные рядом со временем читаются как ещё
         одно двоеточие, а горизонтальное «ещё» — родной паттерн iOS-стилистики. -->
    <div class="flex items-center" style="margin-bottom: 10px;">
      <button @click="emitEditTime" type="button" aria-label="Дата, время и копирование приёма пищи" class="min-h-[44px] flex items-center gap-1.5 text-ink active:opacity-60 transition-opacity">
        <span class="leading-none" style="font-family: Inter, sans-serif; font-size: 19px; font-weight: 600; color: var(--ink);">{{ timeRange }}</span>
        <!-- Впереди запланированы копии этого приёма: детали (какие даты,
             отмена) — в попапе времени, который открывает тот же тап -->
        <svg v-if="groupRepeatAhead" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B98A5E" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/>
          <path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>
        </svg>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="2.2"/><circle cx="12" cy="12" r="2.2"/><circle cx="19" cy="12" r="2.2"/>
        </svg>
      </button>
      <!-- Суммарные КБЖУ (при 2+ блюдах) -->
      <div v-if="dishes.length >= 2 && anyKbjuVisible" class="flex items-center gap-1 ml-auto">
        <span v-if="kbjuVisibility.kcal" class="sum-pill-kcal">{{ totalKcal }}</span>
        <span v-if="kbjuVisibility.protein" class="sum-pill-protein">{{ totalProtein }}</span>
        <span v-if="kbjuVisibility.fat" class="sum-pill-fat">{{ totalFat }}</span>
        <span v-if="kbjuVisibility.carbs" class="sum-pill-carbs">{{ totalCarbs }}</span>
        <span v-if="kbjuVisibility.fiber" class="sum-pill-fiber">
          <template v-if="fiberLoading && groupFiberPending"><span class="inline-block w-2.5 h-2.5 border border-ink/40 border-t-[#41291A] rounded-full animate-spin" /></template>
          <template v-else>{{ totalFiber }}</template>
        </span>
      </div>
    </div>

    <!-- Карточки блюд — 2 в ряд на всю ширину -->
    <div class="grid grid-cols-2" style="gap: 6px;">
      <div
        v-for="(dish, idx) in dishes"
        :key="dish.entries[0].id"
        class="dish-card food-card bg-cream rounded-2xl overflow-hidden relative"
      >
        <button
          data-guide="dish"
          @click="$emit('dish-click', dish)"
          class="w-full text-left active:scale-[0.97] transition-transform"
        >
          <!-- Фото или плейсхолдер -->
          <div class="relative w-full" style="aspect-ratio: 1/1.1;">
            <img v-if="dish.photo_url" :src="imgUrl(dish.photo_url)" alt="" class="food-photo w-full h-full object-cover" />
            <!-- Осветление, виньетка и обводка — общие классы food-* в style.css -->
            <div v-if="dish.photo_url" class="food-vignette" />
            <!-- Без фото (голосовой, ручной ввод): иконки продуктов блюда,
                 до четырёх, сеткой 2×2 по весу ингредиентов (lib/foodIcons.js).
                 Фон — warm (светлее кнопки «+» цвета ink, чтобы не сливались),
                 иконки цветные с тенью, фотоаппарат кремовый (решение владельца
                 8.10.2026) -->
            <div v-else-if="dish.icons.length" class="w-full h-full bg-warm icon-grid" :class="'n' + dish.icons.length">
              <img v-for="k in dish.icons" :key="k" :src="imgUrl('/food-icons/' + k + '.webp')" alt="" loading="lazy" />
            </div>
            <div v-else class="w-full h-full bg-warm flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FAF3E8" stroke-width="1.5" stroke-linecap="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
            <!-- Пилюли КБЖУ -->
            <div v-if="anyKbjuVisible" class="absolute top-1.5 left-1.5 flex flex-col gap-0.5 items-start">
              <span v-if="kbjuVisibility.kcal" class="pill-kcal text-10 font-semibold">{{ dishKcal(dish) }}</span>
              <span v-if="kbjuVisibility.protein" class="pill-protein text-10 font-semibold">{{ dishProtein(dish) }}</span>
              <span v-if="kbjuVisibility.fat" class="pill-fat text-10 font-semibold">{{ dishFat(dish) }}</span>
              <span v-if="kbjuVisibility.carbs" class="pill-carbs text-10 font-semibold">{{ dishCarbs(dish) }}</span>
              <!-- Клетчатка считается позже остальных: у части продуктов её нет
                   ни в одном источнике, и значение приходит ИИ-оценкой уже
                   после загрузки дня. Пока считается — крутилка вместо числа. -->
              <span v-if="kbjuVisibility.fiber" class="pill-fiber text-10 font-semibold flex items-center">
                <template v-if="fiberLoading && dishFiberPending(dish)"><span class="inline-block w-2.5 h-2.5 border border-ink/40 border-t-[#41291A] rounded-full animate-spin" /></template>
                <template v-else>{{ dishFiber(dish) }}</template>
              </span>
            </div>
            <!-- Иконка рецепта -->
            <div
              v-if="dish.entries[0]?.dish_cooked_weight_g > 0"
              class="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center"
            >
              <svg width="16" height="14" viewBox="0 0 14 12" fill="none" stroke="#FAF3E8" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 2C8.72391 2 10.3772 2.68482 11.5962 3.90381C12.8152 5.12279 13.5 6.77609 13.5 8.5C13.5 8.76522 13.3946 9.0196 13.2071 9.2071C13.0196 9.3946 12.7652 9.5 12.5 9.5H1.5C1.23478 9.5 0.98043 9.3946 0.792893 9.2071C0.605357 9.0196 0.5 8.76522 0.5 8.5C0.5 6.77609 1.18482 5.12279 2.40381 3.90381C3.62279 2.68482 5.27609 2 7 2Z"/>
                <path d="M7 2V0.5"/>
                <path d="M0.5 11.5H13.5"/>
              </svg>
            </div>
          </div>

          <!-- Название + вес. Значок повтора — когда впереди запланированы
               копии именно этого блюда (а не всего приёма): значок у времени
               приёма в таком случае не горит -->
          <div class="px-2.5 py-2 bg-white">
            <p class="text-14 font-semibold text-ink leading-tight line-clamp-2" style="min-height: 36px;"><svg v-if="dishRepeatAhead(dish)" class="inline-block mr-0.5 align-[-1px]" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B98A5E" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/>
              <path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>
            </svg>{{ dishLabel(dish) }}</p>
          </div>
        </button>

        <!-- Сердечко — избранное. Показываем и у карточек без блюда:
             продукту из поиска блюдо заводится в момент нажатия. -->
        <button
          @click.stop="toggleFavorite(dish)"
          class="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
        >
          <svg width="18" height="15" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.8131 18.2326C12.5923 19.442 10.7015 19.442 9.48071 18.2326L2.19973 11.019C-2.67113 6.14805 3.68934 -4.6136 11.6469 3.34396C19.5905 -4.59961 25.951 6.16221 21.0941 11.019L13.8131 18.2326Z" :fill="dish.is_favorite ? '#FF6B6B' : 'none'" :stroke="dish.is_favorite ? '#FF6B6B' : '#FAF3E8'" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Ссылки под картинками. «Добавить» — всегда в самой нижней строке
         (владелец, 10.10.2026): рядом с «Голодом» и «Сытостью», если они
         показаны; иначе — в строке «Заметки» или под раскрытой заметкой.
         Все линии здесь — 1,4 px, как ножка букв Inter 400 при 14,5 px:
         иконка «Заметка», кружки голода и сытости, «+» в круге того же
         диаметра 21 px, разделители между приёмами в Diary.vue. -->
    <div class="flex flex-col mt-4" style="gap: 12px;">
      <!-- Заметка -->
      <div class="flex items-center justify-between">
        <textarea
          v-if="noteOpen"
          v-model="noteText"
          ref="noteRef"
          rows="1"
          placeholder="Заметка о приёме пищи..."
          style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 400; color: var(--ink); background: #FEEDB0; border-radius: 12px; padding: 8px 12px; width: 100%; resize: none; outline: none; border: 0; overflow: hidden;"
          @blur="saveNote"
          @click="editingNote = true"
          @input="autoResizeNote"
        />
        <button
          v-else
          @click="startEditNote"
          class="flex items-center gap-2 text-ink"
        >
          <!-- Иконка заметки — блокнот на кольцах (владелец, 10.10.2026);
               цвет — от текста кнопки (text-ink), а не чёрный -->
          <svg width="20" height="20" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 3.5V0.5"/>
            <path d="M7 3.5V0.5"/>
            <path d="M10 3.5V0.5"/>
            <path d="M12.5 2H1.5C0.947715 2 0.5 2.44772 0.5 3V12.5C0.5 13.0523 0.947715 13.5 1.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V3C13.5 2.44772 13.0523 2 12.5 2Z"/>
          </svg>
          <span style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 400; color: var(--ink);">Заметка</span>
        </button>
        <!-- Нижней строки нет (голод и сытость скрыты, заметка свёрнута) —
             «Добавить» здесь, справа от «Заметки» -->
        <button
          v-if="!noteOpen && !showHunger"
          @click="$emit('add', lastEntry.eaten_at)"
          class="flex items-center gap-1.5 text-ink shrink-0 ml-3"
        >
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="9.8"/><line x1="10.5" y1="6.5" x2="10.5" y2="14.5"/><line x1="6.5" y1="10.5" x2="14.5" y2="10.5"/>
          </svg>
          <span style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 400;">Добавить</span>
        </button>
      </div>

      <!-- Нижняя строка: голод и сытость, справа «Добавить» -->
      <div v-if="showHunger || noteOpen" class="flex items-center justify-between">
        <div v-if="showHunger" class="flex items-center" style="gap: 16px;">
          <button @click="pickerType = 'hunger'" class="flex items-center text-ink" style="gap: 6px;">
            <span class="scale-dot" :style="dotStyle(hungerValue)">{{ hungerValue || '' }}</span>
            <span style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 400; color: var(--ink);">Голод</span>
          </button>
          <button @click="pickerType = 'satiety'" class="flex items-center text-ink" style="gap: 6px;">
            <span class="scale-dot" :style="dotStyle(satietyValue)">{{ satietyValue || '' }}</span>
            <span style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 400; color: var(--ink);">Сытость</span>
          </button>
        </div>
        <button
          @click="$emit('add', lastEntry.eaten_at)"
          class="flex items-center gap-1.5 text-ink shrink-0 ml-auto"
        >
          <svg width="21" height="21" viewBox="0 0 21 21" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="9.8"/><line x1="10.5" y1="6.5" x2="10.5" y2="14.5"/><line x1="6.5" y1="10.5" x2="14.5" y2="10.5"/>
          </svg>
          <span style="font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 400;">Добавить</span>
        </button>
      </div>

      <!-- HungerPicker popup -->
      <HungerPicker
        v-if="showHunger && pickerType"
        :type="pickerType"
        :modelValue="pickerType === 'hunger' ? hungerValue : satietyValue"
        @update:modelValue="v => onPickerSave(v, pickerType)"
        @close="pickerType = null"
      />
    </div>

  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue'
import api from '../services/api.js'
import { imgUrl } from '../utils/image.js'
import { ruleSummary } from '../utils/repeatRule.js'
import { fmtQtyUnit } from '../utils/units.js'
import HungerPicker from './HungerPicker.vue'
import { SMILEY_COLORS } from '../utils/smileyUtils.js'

const props = defineProps({
  group: { type: Object, required: true },
  kbjuVisibility: { type: Object, default: () => ({ kcal: true, protein: true, fat: true, carbs: true, fiber: false }) },
  // Идёт ли сейчас дооценка клетчатки — Diary поднимает флаг на время запроса
  fiberLoading: { type: Boolean, default: false },
  showHunger: { type: Boolean, default: true },
  showWeight: { type: Boolean, default: true },
})

const anyKbjuVisible = computed(() =>
  props.kbjuVisibility.kcal || props.kbjuVisibility.protein || props.kbjuVisibility.fat || props.kbjuVisibility.carbs || props.kbjuVisibility.fiber
)

const emit = defineEmits(['add', 'updated', 'group-updated', 'dish-click', 'edit-time', 'favorite-toggled'])


const firstEntry = computed(() => props.group.entries[0])
const lastEntry  = computed(() => props.group.entries.at(-1))

// Запись — следующая порция того же блюда, а не часть текущей карточки.
// Повтор из избранного без правок пишет записи к тому же dish_id, и два кофе
// в одном приёме склеивались в одну карточку на двойной вес, а её правка
// удаляла обе порции и сохраняла одну. Одного признака мало: у 2 % блюд
// продукт в составе дважды (два яйца), а старые добавления шли по записи
// за запрос и растягивались до 6 с — при том что человек повторяет блюдо
// уже через 4–5 с. Вместе они надёжны: продукт уже есть в карточке и
// запись сделана больше чем через секунду (99 % записей одного добавления
// укладываются в 0,44 с, а с 21.09 у пакета created_at вообще общий).
// Без created_at (день из старого кэша) не делим — как было.
function isNextPortion(card, e) {
  if (!card.entries.some(x => x.product_name === e.product_name)) return false
  const prev = Date.parse(card.entries.at(-1).created_at)
  const cur = Date.parse(e.created_at)
  if (!Number.isFinite(prev) || !Number.isFinite(cur)) return false
  return Math.abs(cur - prev) > 1000
}

// Группировка entries → dishes
const dishes = computed(() => {
  const map = new Map()   // ключ блюда → его последняя карточка
  const cards = []
  for (const e of props.group.entries) {
    const key = e.dish_id || `entry-${e.id}`
    let d = map.get(key)
    if (!d || isNextPortion(d, e)) {
      d = {
        dish_id: e.dish_id, entries: [], photo_url: null, icons: [],
        name: null, is_favorite: false,
        // Показанное фото — личная замена картинки продукта. Диалог по этому
        // признаку предлагает вернуть фото из базы.
        product_photo_custom: false,
      }
      map.set(key, d)
      cards.push(d)
    }
    d.entries.push(e)
    if (!d.photo_url) d.photo_url = e.photo_url || e.product_image_url || null
    if (e.dish_name && !d.name) d.name = e.dish_name
    if (e.is_favorite) d.is_favorite = true
    if (e.product_photo_custom) d.product_photo_custom = true
  }
  // Иконки — когда у блюда нет своего фото. Картинка продукта из общей базы
  // при этом не подставляется: у надиктованного кефира она была чужой
  // упаковкой из Open Food Facts (6.10.2026). Остаётся только там, где
  // иконок нет совсем.
  for (const d of cards) {
    const own = d.entries.some(e => e.photo_url)
    if (own) continue
    const keys = []
    for (const e of [...d.entries].sort((a, b) => (b.weight_g || 0) - (a.weight_g || 0))) {
      if (e.icon_key && !keys.includes(e.icon_key)) keys.push(e.icon_key)
      if (keys.length === 4) break
    }
    d.icons = keys
    if (keys.length) d.photo_url = null
  }
  return cards
})

// Агрегации для dish-карточки
function dishKcal(d)    { return Math.round(d.entries.reduce((s, e) => s + (parseFloat(e.kcal) || 0), 0)) }
function dishProtein(d) { return Math.round(d.entries.reduce((s, e) => s + (parseFloat(e.protein) || 0), 0)) }
function dishFat(d)     { return Math.round(d.entries.reduce((s, e) => s + (parseFloat(e.fat) || 0), 0)) }
function dishCarbs(d)   { return Math.round(d.entries.reduce((s, e) => s + (parseFloat(e.carbs) || 0), 0)) }
// Клетчатки в порции обычно единицы граммов, поэтому до десятых — округление
// до целого превратило бы 2,4 в 2 и съело бы половину смысла. От 10 г и выше
// десятые уже не нужны.
function fmtFiber(v)    { return v >= 10 ? Math.round(v) : Math.round(v * 10) / 10 }
function dishFiber(d)   { return fmtFiber(d.entries.reduce((s, e) => s + (parseFloat(e.fiber) || 0), 0)) }
function dishFiberPending(d) { return d.entries.some(e => e.fiber_pending) }
function dishWeight(d) {
  // Если у блюда задан cooked_weight_g (вес порции) — используем его
  const cw = d.entries[0]?.dish_cooked_weight_g
  if (cw && Number(cw) > 0) return Math.round(Number(cw) * 10) / 10
  return Math.round(d.entries.reduce((s, e) => s + (parseFloat(e.weight_g) || 0), 0) * 10) / 10
}

// Суммы КБЖУ по всем entries в группе
const totalKcal    = computed(() => Math.round(props.group.entries.reduce((s, e) => s + (parseFloat(e.kcal) || 0), 0)))
const totalProtein = computed(() => Math.round(props.group.entries.reduce((s, e) => s + (parseFloat(e.protein) || 0), 0)))
const totalFat     = computed(() => Math.round(props.group.entries.reduce((s, e) => s + (parseFloat(e.fat) || 0), 0)))
const totalCarbs   = computed(() => Math.round(props.group.entries.reduce((s, e) => s + (parseFloat(e.carbs) || 0), 0)))
const totalFiber   = computed(() => fmtFiber(props.group.entries.reduce((s, e) => s + (parseFloat(e.fiber) || 0), 0)))
const groupFiberPending = computed(() => props.group.entries.some(e => e.fiber_pending))

function dishLabel(d) {
  const name = d.name || d.entries[0]?.product_name || 'Блюдо'
  if (!props.showWeight) return name
  // Введённое штуками — так и показываем: «Яйцо куриное, 2 шт · 106 г».
  // Только для блюда из одной записи: у составного сумма штук бессмысленна.
  const e = d.entries.length === 1 ? d.entries[0] : null
  if (e && e.qty > 0 && e.unit_name) return `${name}, ${fmtQtyUnit(e.qty, e.unit_name, dishWeight(d))}`
  return `${name}, ${dishWeight(d)}\u00A0г`
}

// Избранное
async function toggleFavorite(dish) {
  try {
    // У продукта, добавленного поиском по базе, блюда нет — сервер заводит его
    // сам по записи. Поэтому у карточки без dish_id сердечко не прячем, а
    // идём другой ручкой: иначе такие продукты в избранное не попадали.
    if (!dish.dish_id) {
      const entryId = dish.entries[0]?.id
      if (!entryId) return
      const { data } = await api.patch(`/diary/entries/${entryId}/favorite`)
      const entry = props.group.entries.find(e => e.id === entryId)
      if (entry) {
        entry.dish_id = data.dish_id
        entry.dish_name = data.dish_name
        entry.is_favorite = data.is_favorite
      }
      emit('favorite-toggled', { dishId: data.dish_id, isFavorite: data.is_favorite })
      return
    }

    const { data } = await api.patch(`/diary/dishes/${dish.dish_id}/favorite`)
    for (const e of props.group.entries) {
      if (e.dish_id === dish.dish_id) {
        e.is_favorite = data.is_favorite
      }
    }
    emit('favorite-toggled', { dishId: data.dish_id, isFavorite: data.is_favorite })
  } catch (err) {
    console.error('Toggle favorite error:', err)
  }
}

function roundTo5Min(date) {
  const d = new Date(date)
  d.setMinutes(Math.round(d.getMinutes() / 5) * 5, 0, 0)
  return d
}

const timeRange = computed(() => {
  const fmt = d => d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
  const startDate = roundTo5Min(new Date(props.group.entries[0].eaten_at))
  const mealEnd = props.group.entries[0].meal_end_at
  const endDate = mealEnd ? roundTo5Min(new Date(mealEnd)) : new Date(startDate.getTime() + 20 * 60000)
  return `${fmt(startDate)} – ${fmt(endDate)}`
})

// Правило дублирования показываем как правило ПРИЁМА, только если все его
// записи состоят в одном наборе: когда дублировали отдельное блюдо, у
// остальных записей набора нет, и чекбоксы приёма должны быть пустыми.
const groupDupRule = computed(() => {
  const dupIds = new Set(props.group.entries.map(e => e.duplicate_group_id).filter(Boolean))
  return (dupIds.size === 1 && props.group.entries.every(e => e.duplicate_group_id))
    ? props.group.entries[0].duplicate_rule
    : null
})

// Значок «будет повторён» у времени приёма: горит, пока впереди остались
// запланированные копии — прошедшее правило приём не помечает
const groupRepeatAhead = computed(() => !!ruleSummary(groupDupRule.value))

// То же для отдельного блюда: когда продублировано оно, а не весь приём.
// При активном значке приёма карточки не дублируют его — двойная маркировка шум
function dishRepeatAhead(dish) {
  if (groupRepeatAhead.value) return false
  const dupIds = new Set(dish.entries.map(e => e.duplicate_group_id).filter(Boolean))
  if (dupIds.size !== 1 || !dish.entries.every(e => e.duplicate_group_id)) return false
  return !!ruleSummary(dish.entries[0].duplicate_rule)
}

function emitEditTime() {
  const startDate = roundTo5Min(new Date(props.group.entries[0].eaten_at))
  const mealEnd = props.group.entries[0].meal_end_at
  const endDate = mealEnd ? roundTo5Min(new Date(mealEnd)) : new Date(startDate.getTime() + 20 * 60000)
  const y = startDate.getFullYear()
  const m = String(startDate.getMonth() + 1).padStart(2, '0')
  const d = String(startDate.getDate()).padStart(2, '0')

  emit('edit-time', {
    entryIds: props.group.entries.map(e => e.id),
    dishCount: dishes.value.length,
    date: `${y}-${m}-${d}`,
    startHour: startDate.getHours(),
    startMin: startDate.getMinutes(),
    endHour: endDate.getHours(),
    endMin: endDate.getMinutes(),
    repeat: groupDupRule.value,
  })
}

// ── PATCH с автоматическим retry (до 2 повторов с паузой) ──
// Гарантирует доставку note/hunger/satiety на сервер при нестабильной сети.
async function patchWithRetry(url, payload, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      await api.patch(url, payload)
      return
    } catch (err) {
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, (attempt + 1) * 1000))
      } else {
        console.error('MealGroup: PATCH failed after retries:', url, payload, err.message)
      }
    }
  }
}

// ── Заметка / голод / сытость ──
// Принадлежат приёму пищи (zoya_meal_groups), а не отдельной записи.
// props.group.meta приходит с сервера; если его нет (записи ещё не
// привязаны к группе — до прогона миграции), откатываемся на старую
// схему «мета на первой записи».
const groupId = computed(() => props.group.id || null)

// Старая схема: ищем мету в ЛЮБОЙ записи группы, а не только в первой.
// Из-за недетерминированного порядка записей с одинаковым eaten_at
// заметка могла оказаться не на первой — и переставала показываться.
const legacyMetaEntry = computed(() =>
  props.group.entries.find(e => e.note || e.hunger_before > 0 || e.satiety_after > 0)
  || firstEntry.value
)

const meta = computed(() => props.group.meta || legacyMetaEntry.value || {})

let unmounted = false
onBeforeUnmount(() => { unmounted = true })

const hungerValue  = ref(meta.value?.hunger_before || null)
const satietyValue = ref(meta.value?.satiety_after || null)
const pickerType   = ref(null)

// Кружок уровня — та же палитра, что в HungerPicker. Без отметки — пустой контур
// цветом иконок-действий рядом, и чуть меньше: пустому кругу не нужно место под цифру.
const EMPTY_RING = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 21 21'%3E%3Ccircle cx='10.5' cy='10.5' r='9.8' fill='none' stroke='%2341291A' stroke-width='1.4'/%3E%3C/svg%3E") center / 100% 100% no-repeat`
function dotStyle(value) {
  return value
    ? { width: '25px', height: '25px', background: SMILEY_COLORS[value], border: 'none' }
    // Обводка — векторным кольцом (SVG-фон), а не border или тенью: их Safari
    // округляет до пикселей экрана (1,4 → 1,5 / 1,33), линии SVG — нет, и
    // кружок совпадает по толщине с «+» в круге и иконкой «Заметка» (1,4 px)
    : { width: '21px', height: '21px', border: 'none', background: EMPTY_RING }
}

// Куда сохранять мету: в приём пищи (новая схема) или в запись (фолбэк).
// Оптимистичный emit идёт ДО PATCH, чтобы данные попали в кэш немедленно
// и не пропали при размонтировании компонента во время запроса.
function saveMeta(payload) {
  const gid = groupId.value
  if (gid) {
    emit('group-updated', { groupId: gid, ...payload })
    patchWithRetry(`/diary/group/${gid}`, payload)
    return
  }
  // Фолбэк для записей без группы: пишем в ту же запись, из которой читаем
  const id = legacyMetaEntry.value?.id ?? firstEntry.value?.id
  if (!id) return
  emit('updated', { id, ...payload })
  patchWithRetry(`/diary/${id}`, payload)
}

async function onPickerSave(val, type) {
  if (unmounted) return
  const field = type === 'hunger' ? 'hunger_before' : 'satiety_after'
  if (type === 'hunger') hungerValue.value = val
  else satietyValue.value = val
  pickerType.value = null
  saveMeta({ [field]: val })
}

const noteText    = ref(meta.value?.note || '')
const editingNote = ref(false)
// Заметка раскрыта: идёт ввод или текст уже есть
const noteOpen = computed(() => editingNote.value || !!noteText.value)
const noteRef     = ref(null)

onMounted(() => {
  if (noteText.value) nextTick(() => autoResizeNote())
})

function startEditNote() {
  editingNote.value = true
  nextTick(() => {
    noteRef.value?.focus()
    autoResizeNote()
  })
}

function autoResizeNote() {
  const el = noteRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

async function saveNote() {
  editingNote.value = false
  if (unmounted) return
  const text = noteText.value.trim()
  noteText.value = text
  saveMeta({ note: text })
}

// ── Синхронизация локальных refs с пропсами при замене данных (network refetch) ──
// loadEntries() заменяет entries/groups свежими объектами с сервера.
// Без этого watcher'а локальные refs остались бы с устаревшими значениями,
// потому что инициализируются однократно при setup.
watch(
  meta,
  (m) => {
    if (!m) return
    if (!editingNote.value) {
      noteText.value = m.note || ''
    }
    hungerValue.value = m.hunger_before || null
    satietyValue.value = m.satiety_after || null
  },
)
</script>

<style scoped>
.pill-kcal    { background: #EE998C; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-protein { background: #9FC2C1; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fat     { background: #EBC06D; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-carbs   { background: #EEAD77; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fiber   { background: #A6B779; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.sum-pill-kcal    { background: #EE998C; border-radius: 8px; padding: 2px 8px; color: var(--ink); font-size: var(--fs-12); font-weight: 600; font-family: Inter, sans-serif; opacity: 0.9; }
.sum-pill-protein { background: #9FC2C1; border-radius: 8px; padding: 2px 8px; color: var(--ink); font-size: var(--fs-12); font-weight: 600; font-family: Inter, sans-serif; opacity: 0.9; }
.sum-pill-fat     { background: #EBC06D; border-radius: 8px; padding: 2px 8px; color: var(--ink); font-size: var(--fs-12); font-weight: 600; font-family: Inter, sans-serif; opacity: 0.9; }
.sum-pill-carbs   { background: #EEAD77; border-radius: 8px; padding: 2px 8px; color: var(--ink); font-size: var(--fs-12); font-weight: 600; font-family: Inter, sans-serif; opacity: 0.9; }
.sum-pill-fiber   { background: #A6B779; border-radius: 8px; padding: 2px 8px; color: var(--ink); font-size: var(--fs-12); font-weight: 600; font-family: Inter, sans-serif; opacity: 0.9; display: inline-flex; align-items: center; }
textarea::placeholder { color: var(--ink-70); }

/* Отметка шкалы голода-сытости: цифра в цветном круге (палитра HungerPicker) */
.scale-dot {
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: var(--fs-13);
  font-weight: 700;
  line-height: 1;
  color: var(--ink);
}

/* Сетка иконок: одна — крупно по центру, две — в ряд, три-четыре — 2×2.
   Отступ слева оставляет место пилюлям КБЖУ в верхнем левом углу. */
/* Иконки бледные, как плейсхолдер с камерой: набор яркий и контрастный, а
   карточка без фото должна читаться тише фотографии (правка владельца
   8.10.2026). Сетка — по центру, зазор одинаковый по обеим осям: строки
   не растягиваются на высоту ячейки, а сжимаются к содержимому. */
.icon-grid { display: grid; gap: 4px; padding: 14%; align-content: center; justify-content: center; align-items: center; justify-items: center; }
.icon-grid img { width: 100%; height: auto; aspect-ratio: 1; object-fit: contain; filter: drop-shadow(0 3px 4px rgba(0,0,0,.35)); }
.icon-grid.n1 { grid-template-columns: 42%; }
.icon-grid.n2 { grid-template-columns: 27% 27%; }
.icon-grid.n3, .icon-grid.n4 { grid-template-columns: 27% 27%; grid-auto-rows: min-content; }
.icon-grid.n3 img:first-child { grid-column: 1 / span 2; width: 50%; }

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
